import { useView } from "aurelia-framework";
import { autoinject, transient } from "aurelia-dependency-injection";
import { activationStrategy } from "aurelia-router";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { CustomerDetailsModel } from "../../../../../../../../models/customers/customer.models";
import { MepPortDetails, MepGetVesselGroupItemResponse } from "../../../../../../../../models/mep/mep.models";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { AlphaNumericSortHelper } from "../../../../../../../../helpers/alpha-numeric-sort-helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
const leaflet = await import('leaflet');

@autoinject
@transient()
@useView('views/pages/modules/mep/tabs/mep-home/tabs/mep-about/mep-about.html')
export class MepAbout {
    public portId: number;
    public customerId?: number;
    public portDetails: MepPortDetails;
    public markers: any[] = [];
    public map: any;
    public mapPrint: any;
    public geoJsonPortArea: any;
    public mapLayer: any;
    public satelliteLayer: any;
    vesselTypeGroups: MepGetVesselGroupItemResponse[];

    public showPolygons: boolean = true;
    public showPointsOfInterest: boolean = true;
    public showMapView: boolean = true;

    //Claims
    canGeneratePortDashboardReport: boolean;

    customerDetails: CustomerDetailsModel;

    constructor(private mepService: MepService, private notificationClientService: NotificationClientService,
        private dialogClientService: DialogClientService, userClaimsService: UserClaimsService, private configService: ConfigService) {
        this.canGeneratePortDashboardReport = userClaimsService.hasPermission(Claims.CanGeneratePortDashboardReport);
    }

    activate(param) {
        this.portId = param.portId;
        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(window["rightshipUser"]);
    }

    attached() {
        this.loadPortDetails();
    }

    loadPortDetails()
    {
        this.mepService.getMepPortDetails(this.portId, true).then((x: MepPortDetails) => {
            this.portDetails = x;
        
            this.initializeMap();
        }).catch(() => {
            this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
        });
    }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    initializeMap() {
        if(this.map)
        {
            this.map.off();
            this.map.remove();
        }
        const isMobile = MobileHelper.isMobileDevice();
        this.map = leaflet.map("map", { center: [51.505, -0.09], zoom: 13, dragging: !isMobile, tap: !isMobile, gestureHandling: true });
        leaflet.control.scale().addTo(this.map);
        this.createLayers(this.map);
        this.geoJsonPortArea = MepAbout.createPortArea(this.map, this.portDetails);
        this.markers = MepAbout.createPointsOfInterest(this.map, this.portDetails);
        
        this.map.fitBounds(this.geoJsonPortArea.getBounds());
    }

    initializeMapPrint() {
        const isMobile = MobileHelper.isMobileDevice();
        this.mapPrint = leaflet.map("mapprint", { center: [51.505, -0.09], zoom: 13, dragging: !isMobile, tap: !isMobile, gestureHandling: true });
        leaflet.control.scale().addTo(this.mapPrint);
        this.createLayers(this.mapPrint);
        this.geoJsonPortArea = MepAbout.createPortArea(this.mapPrint, this.portDetails);
        this.markers = MepAbout.createPointsOfInterest(this.mapPrint, this.portDetails);
        
        this.mapPrint.fitBounds(this.geoJsonPortArea.getBounds());
    }

    meters2degress(x:number,y: number) {
        const lon = x *  180 / 20037508.34 ;
        //thanks magichim @ github for the correction
        const lat = Math.atan(Math.exp(y * Math.PI / 20037508.34)) * 360 / Math.PI - 90; 
        return [lon, lat]
    }

    createLayers(map: any) {
        if (!this.configService.config.urls.leafletMapTilesUrl || !this.configService.config.settings.leafletAccessToken)
            return;

        this.mapLayer = leaflet.tileLayer(this.configService.config.urls.leafletMapTilesUrl, {
            attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
            maxZoom: 18,
            id: 'mapbox/streets-v11',
            tileSize: 512,
            zoomOffset: -1,
            accessToken: this.configService.config.settings.leafletAccessToken
        }).addTo(map);

        this.satelliteLayer = leaflet.tileLayer(this.configService.config.urls.leafletMapTilesUrl, {
            attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
            maxZoom: 18,
            id: 'mapbox/satellite-v9',
            tileSize: 512,
            zoomOffset: -1,
            accessToken: this.configService.config.settings.leafletAccessToken
        });
    }

    static createPortArea(map: any, portDetails: MepPortDetails) : any {
        if (!portDetails)
            return;

        const myStyle = {
            "color": "#ff7800",
            "weight": 2,
            "opacity": 0.65
        };
        const polygon = MepAbout.createPolygon(myStyle, portDetails.polygonGeoJson, map);
        polygon.bindPopup(portDetails.portStudyAreaName, {autoPan: false});
        polygon.on('mouseover', (e) => {polygon.openPopup(e.latlng)});
        polygon.on('mouseout', () => {polygon.closePopup()});
        polygon.bringToFront();
        return polygon;
    }

    static createPolygon(style: any, polygonGeoJson: string, map: any): any {
        if(style && polygonGeoJson){
            const json = JSON.parse(polygonGeoJson);
            const result = leaflet.geoJSON(json, {
                style: style
            }).addTo(map);
            
            return result;
        }
    }

    static createPointsOfInterest(map: any, portDetails: MepPortDetails): any[]  {
        if (!portDetails || !portDetails.pointsOfInterest) 
            return;

        const markers: any[] = [];
        const defaultStyle = {
            "color": "#0078ff",
            "weight": 4,
            "opacity": 0.2
        };

        portDetails.pointsOfInterest.forEach(pointOfInterest => {
            if (pointOfInterest) {
                const poi = this.createPolygon(defaultStyle, pointOfInterest.pointOfInterestPolygon, map);
                poi.bindPopup(pointOfInterest.pointOfInterestName, { autoPan: false });
                poi.on('mouseover', (e) => {poi.openPopup(e.latlng)});
                poi.on('mouseout', () => {poi.closePopup()});
                // poi.setZIndex(9999);
                poi.bringToFront();
                markers.push(poi);
            }
        });

        return markers;
    }

    togglePolygons(map: any = this.map) {
        if (!this.geoJsonPortArea) {
            this.geoJsonPortArea = MepAbout.createPortArea(map, this.portDetails);
        }

        if (!this.showPolygons) {
            this.geoJsonPortArea.removeFrom(map);
        }
        else {
            //The following was done as POIs should ways be above the Ports
            if(this.showPointsOfInterest) {

                //First remove Points of interest markers
                this.markers.forEach(marker => {
                    marker.removeFrom(map);
                });

                //Add Ports
                this.geoJsonPortArea.addTo(map);

                //Re-add Points of intrest markets
                this.markers.forEach(marker => {
                    marker.addTo(map);
                });
            }
            else {

                //Add Ports
                this.geoJsonPortArea.addTo(map);
            }
        }
    }

    togglePointsOfInterest(map: any = this.map) {
        if (!this.markers) {
            this.markers = MepAbout.createPointsOfInterest(map, this.portDetails);
        }

        if (!this.showPointsOfInterest) {
            this.markers.forEach(marker => {
                marker.removeFrom(map);
            });
        }
        else {
            this.markers.forEach(marker => {
                marker.addTo(map);
            });
        }
    }

    toggleSatelliteView(map: any = this.map) {
        if (this.showMapView) {
            this.satelliteLayer?.addTo(map);
            this.mapLayer?.removeFrom(map);
        }
        else {
            this.mapLayer?.addTo(map);
            this.satelliteLayer?.removeFrom(map);
        }
    }

    generateReport() {
        if(this.portDetails && this.map) {

            //Get VesselTypeGroups for printing
            this.mepService.getVesselTypeGroups(this.portId, null)
                .then(x => {
                    if (!x.hasError) {
                        this.vesselTypeGroups = x.items.sort((a,b) => AlphaNumericSortHelper.alphaNumericSort(a, b, 'vesselTypeGroupName'));
                        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PrintMepDocumentationDialog, this, true, false, true);
                    } else {
                        this.notificationClientService.show("MEP Error", "Error loading vessel type groups!", NotificationType.Error);
                    }

                }).catch(() => {
                    this.notificationClientService.show("MEP Error", "Error loading vessel type groups!", NotificationType.Error);
                });

        }
        else {
            this.notificationClientService.show("Page is loading, please try again once its loaded", "", NotificationType.Info);
        }
    }

    detached() {
        if(this.map)
        {
            this.map.off();
            this.map.remove();
            this.map = null;
        }
    }

    redirect(link: string, title: string='blank') {
        const newWindow = window.open();
        newWindow.document.title = title;
        newWindow.document.write(`<html><head><title>${title}</title></head><body><iframe src=${link} style="position:fixed; top:0; left:0; bottom:0; right:0; width:100%; height:100%; 
                border:none; margin:0; padding:0; overflow:hidden; z-index:999999;"></iframe></body></html>`);
    }

    refresh(data?: {portId: number})
    {
        if(this.portDetails)
        {
            this.portId = data.portId;
            this.loadPortDetails();
        }
    }
}
